import { css } from 'lit';
import { base, reset } from '../../css';
import { color, rem } from '../../css.helper';
import { Theme } from '../../theme.helper';

const theme = new Theme('a-dialog');

export const TmDialogElementStyle = [
  base.focus,
  reset.button,
  css`
    :host {
      display: block;
    }

    :where(dialog) {
      background: none;
      border: 0;
      padding: 0;
      box-shadow: var(--tm-elevation-shadow-overlay);
    }

    :where(dialog.hide) {
      animation: fade-out var(--tm-transition-duration-normal)
        var(--tm-transition-function-ease-in-out) forwards !important;
    }

    :where(dialog[open]) {
      animation: fade-in var(--tm-transition-duration-normal)
        var(--tm-transition-function-ease-in-out) forwards;
    }

    :where(dialog.hide)::backdrop {
      animation: backdrop-fade-out var(--tm-transition-duration-normal)
        var(--tm-transition-function-ease-in-out) forwards !important;
    }

    :where(dialog[open])::backdrop {
      animation: backdrop-fade-in var(--tm-transition-duration-normal)
        var(--tm-transition-function-ease-in-out) forwards;
    }

    @keyframes backdrop-fade-in {
      from {
        background: transparent;
      }
      to {
        background: var(--tm-elevation-backdrop-color);
      }
    }

    @keyframes backdrop-fade-out {
      from {
        background: var(--tm-elevation-backdrop-color);
      }
      to {
        background: transparent;
      }
    }

    @keyframes fade-in {
      from {
        opacity: 0;
      }
      to {
        opacity: 1;
      }
    }

    @keyframes fade-out {
      from {
        opacity: 1;
        display: block;
      }
      to {
        opacity: 0;
        display: none;
      }
    }

    :where(.trigger) {
      appearance: none;
      background: none;
      border: 0;
      cursor: pointer;
      display: block;
      padding: 0;
    }

    :where(button.close) {
      align-items: center;
      background: none;
      block-size: ${rem(40)};
      border: 0 solid;
      box-shadow: var(--tm-elevation-shadow-overlay);
      cursor: pointer;
      display: flex;
      font-size: ${rem(24)};
      inline-size: ${rem(40)};
      inset-block-start: ${rem(20)};
      inset-inline-end: ${rem(20)};
      justify-content: center;
      position: fixed;
      z-index: 1;

      ${theme.props([
        ['background', 'close-button-background', color('white')],
        ['color', 'close-button-color', color('black')],
        ['border-radius', 'close-button-border-radius', '50%'],
        ['border-width', 'close-button-border-width', rem(0)],
        ['border-color', 'close-button-border-color', color('white')],
        [
          'transition',
          'close-button-transition',
          'all var(--tm-transition-duration-normal) var(--tm-transition-function-ease-out)',
        ],
      ])};
    }

    :where(.close-button-wrapper) {
      position: absolute;
      inset-block-start: ${rem(4)};
      inset-inline-end: ${rem(4)};
      inline-size: ${rem(44)};
    }

    :where(button.close-contained) {
      align-items: center;
      border: 0 solid;
      cursor: pointer;
      display: flex;
      font-size: ${rem(20)};
      padding: ${rem(12)};
      position: fixed;

      ${theme.props([
        ['background', 'close-button-background', color('white')],
        ['color', 'close-button-color', color('black')],
        [
          'transition',
          'close-button-transition',
          'all var(--tm-transition-duration-normal) var(--tm-transition-function-ease-out)',
        ],
      ])};
    }

    :where(container) {
      position: relative;
    }

    :where(header) {
      display: flex;

      button {
        align-items: center;
        border-radius: ${rem(4)};
        display: flex;
        margin-inline-start: auto;
        padding-inline: ${rem(4)};

        &:hover {
          background: ${color('grey', 100)};
        }
      }
    }

    @media (hover: hover) {
      :where(button.close):hover {
        ${theme.props([
          ['background', 'close-button-background-hover', color('blue', 200)],
          ['color', 'close-button-color-hover', color('black')],
        ])}
      }
    }

    :where(.controls) {
      align-items: center;
      box-shadow: var(--tm-elevation-shadow-overlay);
      display: flex;
      inset-block-end: ${rem(24)};
      inset-inline-start: 50%;
      justify-content: center;
      position: fixed;
      translate: -50%;
      z-index: 1;

      ${theme.props([
        ['background', 'controls-background', color('white')],
        ['gap', 'controls-gap', rem(8)],
        ['padding', 'controls-padding', rem(8)],
        ['border-radius', 'controls-border-radius', rem(56)],
      ])};
    }

    :where(.control-button) {
      align-items: center;
      border: 0;
      cursor: pointer;
      display: flex;
      font-size: ${rem(20)};

      ${theme.props([
        ['background', 'control-button-background', 'transparent'],
        ['color', 'control-button-color', color('black')],
        ['border-radius', 'control-button-border-radius', '50%'],
        ['padding', 'control-button-padding', rem(10)],
        [
          'transition',
          'close-button-transition',
          'all var(--tm-transition-duration-normal) var(--tm-transition-function-ease-out)',
        ],
      ])};
    }

    :where(.control-button[disabled]) {
      cursor: not-allowed;
      opacity: 0.5;
    }

    @media (hover: hover) {
      :where(.control-button:is(:not([disabled]))):hover {
        ${theme.props([
          ['background', 'control-button-background-hover', color('blue', 200)],
        ])};
      }
    }

    :host([disabled]) {
      button {
        opacity: 0.2;
        pointer-events: none;
      }
    }
  `,
];
